import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { X, Eye, Users, Cpu, Loader2 } from "lucide-react";
import {
  SocmintPanel, OsintPanel, HumintPanel,
  IntelligenceToolbar, buildOsintFilter, buildSocmintFilter, buildHumintFilter, buildSort,
} from "./IntelligencePanels";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SECTIONS = [
  { key: "osint",   label: "OSINT",   color: "#0891B2", icon: Eye,   desc: "Open Source Intelligence — dati pubblici e web-scraped" },
  { key: "socmint", label: "SOCMINT", color: "#059669", icon: Users, desc: "Social Media Intelligence — LinkedIn, X, YouTube" },
  { key: "humint",  label: "HUMINT",  color: "#EA580C", icon: Cpu,   desc: "Human Intelligence — insight relazionali qualificati" },
];

const EMPTY_INTEL = { osint: [], socmint: [], humint: [] };

/**
 * IntelligenceModal — mostra la scheda intel unificata (OSINT/SOCMINT/HUMINT)
 * per una persona o azienda. Supera la suddivisione Sales/Supplier.
 *
 * Props:
 *  - targetId (string)
 *  - targetType ("person" | "company")
 *  - title (string) — heading (nome persona / azienda)
 *  - subtitle (string) — ruolo/azienda o settore/HQ
 *  - onClose (fn)
 *  - children (ReactNode) — contenuto extra (dati anagrafici, KPI, KYC, bilancio)
 */
export default function IntelligenceModal({
  targetId, targetType, title, subtitle, onClose, children,
}) {
  const [intel, setIntel] = useState(EMPTY_INTEL);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("osint");
  const [filters, setFilters] = useState({ text: "", minImportance: 0, platform: "all", category: "all", channel: "all", sort: "default" });

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/intelligence/${targetType}/${targetId}`, { withCredentials: true });
      setIntel({
        osint: Array.isArray(data.osint) ? data.osint : [],
        socmint: Array.isArray(data.socmint) ? data.socmint : [],
        humint: Array.isArray(data.humint) ? data.humint : [],
      });
    } catch {
      setIntel(EMPTY_INTEL);
    } finally { setLoading(false); }
  }, [targetId, targetType]);

  useEffect(() => { load(); }, [load]);

  const osintFilter   = useMemo(() => buildOsintFilter(filters),   [filters]);
  const socmintFilter = useMemo(() => buildSocmintFilter(filters), [filters]);
  const humintFilter  = useMemo(() => buildHumintFilter(filters),  [filters]);
  const sortFn        = useMemo(() => buildSort(filters),          [filters]);
  const counts = useMemo(() => ({
    osint:   { total: intel.osint.length,   visible: intel.osint.filter(osintFilter).length },
    socmint: { total: intel.socmint.length, visible: intel.socmint.filter(socmintFilter).length },
    humint:  { total: intel.humint.length,  visible: intel.humint.filter(humintFilter).length },
  }), [intel, osintFilter, socmintFilter, humintFilter]);

  return (
    <div
      data-testid="intel-modal"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0C10] border border-white/[0.04] w-full max-w-6xl sm:my-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0A0C10] border-b border-white/[0.04] px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold mb-1">
              Intelligence · {targetType === "person" ? "People Card" : "Business Card"}
            </p>
            <h2 className="text-2xl font-bold text-white tracking-tight leading-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
            {subtitle && <p className="text-sm text-[#A0AEC0] mt-1 truncate">{subtitle}</p>}
          </div>
          <button
            data-testid="intel-modal-close"
            onClick={onClose}
            className="flex-shrink-0 p-2 border border-white/[0.04] bg-[#050505] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#CBD5E1] transition-colors"
            aria-label="Chiudi scheda"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Extra content (identity / KYC / financials) */}
        {children && (
          <div className="px-6 py-5 border-b border-white/[0.04]">{children}</div>
        )}

        {/* Intelligence section */}
        <div className="px-6 py-5">
          <div className="mb-3 flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#A0AEC0] font-bold mb-1">Intelligence Feed</p>
              <p className="text-xs text-[#6B7280]">Ricerche OSINT · SOCMINT · HUMINT unificate — nessuna divisione Sales/Supplier</p>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-[#6B7280] tabular-nums">
              Totale entries: <span className="text-white font-bold">{intel.osint.length + intel.socmint.length + intel.humint.length}</span>
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-14 text-[#A0AEC0] gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
              <span className="text-xs tracking-widest uppercase">Caricamento intelligence…</span>
            </div>
          ) : (
            <>
              <IntelligenceToolbar filters={filters} setFilters={setFilters} counts={counts} />

              <div className="bg-[#0D0F12] border border-white/[0.04] overflow-hidden mt-3">
                {/* Tabs */}
                <div className="grid grid-cols-3 border-b border-white/[0.04]">
                  {SECTIONS.map((sec) => {
                    const isActive = activeTab === sec.key;
                    const c = counts[sec.key];
                    return (
                      <button
                        key={sec.key}
                        data-testid={`intel-modal-tab-${sec.key}`}
                        onClick={() => setActiveTab(sec.key)}
                        className="relative flex items-center justify-center gap-2 py-3.5 transition-colors hover:bg-[#12161C]"
                        style={{
                          borderBottom: `3px solid ${isActive ? sec.color : "transparent"}`,
                          background: isActive ? `${sec.color}10` : undefined,
                        }}
                      >
                        <sec.icon className="w-4 h-4" style={{ color: isActive ? sec.color : "#A0AEC0" }} />
                        <span className="text-sm font-bold tracking-widest uppercase" style={{ fontFamily: "Chivo, sans-serif", color: isActive ? sec.color : "#CBD5E1" }}>
                          {sec.label}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 border tabular-nums font-bold" style={{
                          color: sec.color, borderColor: `${sec.color}55`, background: `${sec.color}15`,
                        }}>
                          {c.visible}{c.visible !== c.total && ` / ${c.total}`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Description */}
                <div className="px-5 py-3 border-b border-white/[0.04] bg-[#0A0C10]">
                  {SECTIONS.filter(s => s.key === activeTab).map(s => (
                    <p key={s.key} className="text-xs text-[#A0AEC0] leading-relaxed">
                      <span className="font-bold uppercase tracking-widest mr-2" style={{ color: s.color }}>{s.label}</span>
                      {s.desc}
                    </p>
                  ))}
                </div>

                {/* Panel — read-only friendly (setter no-op since this modal is read-only) */}
                <div className="p-5">
                  {activeTab === "osint" && (
                    <OsintPanel data={intel.osint} filter={osintFilter} sort={sortFn} onChange={() => {}} />
                  )}
                  {activeTab === "socmint" && (
                    <SocmintPanel data={intel.socmint} filter={socmintFilter} sort={sortFn} onChange={() => {}} />
                  )}
                  {activeTab === "humint" && (
                    <HumintPanel data={intel.humint} filter={humintFilter} sort={sortFn} onChange={() => {}} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
